/**
 * Dialog "Nova conversa" (WhatsApp ATIVO, v8 Etapa 3b): canal conectado +
 * telefone E.164 + modelo HSM (fora da janela de 24h só modelo abre conversa).
 * 409 chat_already_open → abre a conversa existente (details carrega o id).
 *
 * Spec 025: os modelos oferecidos são os do NÚMERO escolhido — o da Blip lista
 * os da conta da Blip (pelo broker), o da Meta os da Meta. Trocar de canal
 * recomeça a escolha do modelo.
 */
import { useState, type ReactNode } from 'react';
import type { WhatsappChannel } from '@erp/shared';
import { sendablePhone } from '@erp/shared';
import type { ApiError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Dialog, Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { useStartWhatsappChat, type ChatTemplateBody } from './chats-mutations';
import { TemplatePicker } from './whatsapp-template-picker';
import { errorMessage } from './chats-format';

/**
 * O telefone sai pela MESMA regra do servidor e do disparo (`sendablePhone`):
 * 55 + DDD + número, com ou sem o 55 digitado. 20/10: "31 98752-4881" passava
 * aqui e saía para +31 (Holanda), sem erro nenhum.
 */
function formatWhatsappPhone(p: string): string {
  return `+${p.slice(0, 2)} ${p.slice(2, 4)} ${p.slice(4, -4)}-${p.slice(-4)}`;
}

export function NewWhatsappChatDialog({
  channels,
  initialPhone,
  phoneHint,
  phoneSource,
  link,
  onClose,
  onOpened,
}: {
  /** canais WhatsApp ativos (GET /whatsapp/channels, filtrado no chamador) */
  channels: WhatsappChannel[];
  /**
   * Telefone pré-preenchido (aba "Conversas" do ticket, v12: contato do
   * cliente). É só um ponto de partida EDITÁVEL — a validação E.164 é a mesma
   * do campo digitado à mão.
   */
  initialPhone?: string;
  /**
   * Recado acima do campo quando NÃO foi possível pré-preencher (v12: cliente
   * sem telefone no cadastro). O fluxo segue aberto — o atendente costuma ter
   * o número mesmo com o cadastro desatualizado.
   */
  phoneHint?: string;
  /** spec 057: de onde veio o número pré-preenchido (ex.: "telefone da ficha") */
  phoneSource?: string;
  /** spec 057: aberta de dentro do ticket — a conversa nasce vinculada a ele */
  link?: { ticketNumber: number; clientId: number };
  onClose: () => void;
  /** existing=true quando a API respondeu 409 chat_already_open com o id */
  onOpened: (chatId: number, existing: boolean) => void;
}) {
  const start = useStartWhatsappChat();

  const [channelId, setChannelId] = useState(() =>
    channels.length === 1 ? String(channels[0]?.id ?? '') : ''
  );
  const [phone, setPhone] = useState(initialPhone ?? '');
  const [template, setTemplate] = useState<ChatTemplateBody | null>(null);
  const [error, setError] = useState<string | null>(null);
  const channel = channels.find((c) => String(c.id) === channelId) ?? null;

  const submit = () => {
    setError(null);
    if (channelId === '') {
      setError('Selecione o canal de envio.');
      return;
    }
    // aceita "+55 (48) 99999-8888", "48 99999-8888" e afins
    const to = sendablePhone(phone);
    if (to === null) {
      setError('Telefone inválido. Informe o DDD e o número (ex.: 31 98752-4881).');
      return;
    }
    if (!template) {
      setError('Selecione um modelo e preencha todos os parâmetros.');
      return;
    }
    start.mutate(
      { channelId: Number(channelId), to, template, ...(link ?? {}) },
      {
        onSuccess: ({ id }) => onOpened(id, false),
        onError: (e) => {
          const err = e as unknown as ApiError;
          if (err.status === 409 && err.error === 'chat_already_open') {
            const details = err.details as { id?: number; chatId?: number } | undefined;
            const existingId = details?.id ?? details?.chatId;
            if (typeof existingId === 'number') {
              onOpened(existingId, true);
              return;
            }
          }
          setError(errorMessage(e));
        },
      }
    );
  };

  return (
    <Dialog open onClose={onClose} title="Nova conversa">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <p className="text-sm text-muted-foreground">
          Inicie uma conversa ativa no WhatsApp enviando um modelo aprovado para o número
          informado.
        </p>

        <Field label="Canal" htmlFor="nw-channel" required>
          <Select
            id="nw-channel"
            className="h-8"
            value={channelId}
            onChange={(e) => {
              setChannelId(e.target.value);
              // o modelo escolhido era do número anterior
              setTemplate(null);
            }}
            disabled={start.isPending}
          >
            <option value="">Selecione…</option>
            {channels.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayPhone}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Telefone" htmlFor="nw-phone" required>
          {(phoneHint ?? phoneSource) ? (
            <p className="text-xs text-muted-foreground">{phoneHint ?? phoneSource}</p>
          ) : null}
          <Input
            id="nw-phone"
            className="h-8"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="31 98752-4881"
            inputMode="tel"
            // com a dica na tela não há número a pré-preencher: o cursor começa
            // exatamente onde falta digitar (o /chats segue como estava)
            autoFocus={channels.length !== 1 || phoneHint !== undefined}
            disabled={start.isPending}
          />
          {sendablePhone(phone) ? (
            <p className="text-xs text-muted-foreground" data-testid="nw-phone-preview">
              Vai para {formatWhatsappPhone(sendablePhone(phone)!)}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">DDD e número. O 55 do Brasil entra sozinho.</p>
          )}
        </Field>

        <TemplatePicker
          // um picker por canal: a escolha e os parâmetros recomeçam ao trocar
          key={channelId}
          idPrefix="nw-tpl"
          phoneNumberId={channel?.phoneNumberId ?? null}
          onChange={setTemplate}
          disabled={start.isPending}
        />

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={start.isPending}>
            {start.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
            Iniciar conversa
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function Field({
  label,
  htmlFor,
  required,
  className,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor} className="text-[13px] font-semibold">
        {label}
        {required ? <span className="-ml-1 text-destructive">*</span> : null}
      </Label>
      {children}
    </div>
  );
}
